export type SegmentAppendResult = "applied" | "already-applied";

export type SegmentUploadFailure = "header-lost" | "chunk-exhausted" | "backlog-overflow";

export interface SegmentUploadQueueOptions {
    sasUrl: string;
    maxAttempts: number;
    backoffBaseMs: number;
    maxBackoffMs: number;
    maxQueuedBytes: number;
    append: (
        uploadUrl: string,
        chunk: Blob,
        appendPosition: number | null
    ) => Promise<SegmentAppendResult>;
    delay: (ms: number) => Promise<void>;
    onFailure: (failure: SegmentUploadFailure) => void;
}

export class SegmentUploadQueue {
    private _queue: Promise<void> = Promise.resolve();
    private _appendPosition = 0;
    private _appendedBytes = 0;
    private _queuedBytes = 0;
    private _chunkCount = 0;
    private _failure: SegmentUploadFailure | null = null;
    private _accepting = true;
    private _abandoned = false;
    private _usesAppendPosition = true;

    constructor(private readonly _options: SegmentUploadQueueOptions) {}

    get appendedBytes(): number {
        return this._appendedBytes;
    }

    get queuedBytes(): number {
        return this._queuedBytes;
    }

    get chunkCount(): number {
        return this._chunkCount;
    }

    get failure(): SegmentUploadFailure | null {
        return this._failure;
    }

    get accepting(): boolean {
        return this._accepting;
    }

    disableAppendPosition(): void {
        this._usesAppendPosition = false;
    }

    enqueue(chunk: Blob): void {
        if (!this._accepting) {
            return;
        }

        if (this._chunkCount > 0 && this._queuedBytes + chunk.size > this._options.maxQueuedBytes) {
            this._fail("backlog-overflow");
            return;
        }

        this._chunkCount++;
        this._queuedBytes += chunk.size;
        this._queue = this._queue.then(() => this._appendWithBackoff(chunk));
    }

    async drain(): Promise<void> {
        let awaited = this._queue;
        for (;;) {
            await awaited.then(
                () => undefined,
                () => undefined
            );

            if (this._queue === awaited) {
                return;
            }

            awaited = this._queue;
        }
    }

    abandon(): void {
        this._accepting = false;
        this._abandoned = true;
        this._queuedBytes = 0;
        this._queue = Promise.resolve();
    }

    private async _appendWithBackoff(chunk: Blob): Promise<void> {
        try {
            if (this._abandoned) {
                return;
            }

            const isHeaderChunk = this._appendedBytes === 0;

            for (let attempt = 0; attempt < this._options.maxAttempts; attempt++) {
                try {
                    const uploadUrl = SegmentUploadQueue.buildAppendUrl(this._options.sasUrl);
                    const position = this._usesAppendPosition ? this._appendPosition : null;
                    await this._options.append(uploadUrl, chunk, position);

                    this._appendPosition += chunk.size;
                    this._appendedBytes += chunk.size;
                    return;
                } catch {
                    if (this._abandoned) {
                        return;
                    }

                    if (attempt === this._options.maxAttempts - 1) {
                        this._fail(isHeaderChunk ? "header-lost" : "chunk-exhausted");
                        return;
                    }

                    await this._options.delay(this._backoffFor(attempt));
                }
            }
        } finally {
            this._queuedBytes = Math.max(0, this._queuedBytes - chunk.size);
        }
    }

    private _backoffFor(attempt: number): number {
        return Math.min(this._options.backoffBaseMs * 2 ** attempt, this._options.maxBackoffMs);
    }

    private _fail(failure: SegmentUploadFailure): void {
        if (this._failure) {
            return;
        }

        this._failure = failure;
        this._accepting = false;
        this._options.onFailure(failure);
    }

    private static buildAppendUrl(sasUrl: string): string {
        return sasUrl.includes("?") ? `${sasUrl}&comp=appendblock` : `${sasUrl}?comp=appendblock`;
    }
}
